import { useState } from 'react';
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import SuperAdminLayout from "@/components/admin/super-admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Building,
  UserCog,
  Plus,
  Search,
  Users,
  CreditCard,
  Calendar,
  Mail,
  MoreHorizontal,
  ShieldCheck,
  Eye,
  Trash2,
  ArrowUpDown,
  Filter,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

// Tenant type (removed domain field)
interface Tenant {
  id: number;
  name: string;
  status: 'active' | 'inactive' | 'suspended' | 'trial' | 'frozen';
  createdAt: string;
  trialEndsAt?: string | null;
  planId?: number | null;
  userCount: number;
  licenseCount: number;
  planType: string;
  planName: string;
  stripeCustomerId?: string;
  ownerFullName?: string;
  ownerEmail?: string;
  ownerUsername?: string;
  primaryContactEmail?: string;
  otherEmails?: string[];
}

function isTrialExpired(tenant: Tenant): boolean {
  if (tenant.planId && tenant.planId >= 2) return false; // paid plan, not a trial
  if (!tenant.trialEndsAt) return false;
  return new Date(tenant.trialEndsAt) < new Date();
}

export default function TenantsPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [showAddTenantDialog, setShowAddTenantDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showTenantDetails, setShowTenantDetails] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Tenant form state (removed domain field)
  const [tenantForm, setTenantForm] = useState({
    name: '',
    planType: 'free',
    status: 'active'
  });
  
  // State for viewing tenant details
  const [isViewingDetails, setIsViewingDetails] = useState(false);

  // Manual activation dialog state
  const [activateDialogTenant, setActivateDialogTenant] = useState<Tenant | null>(null);
  const [activatePlan, setActivatePlan] = useState('single');
  const [activateBilling, setActivateBilling] = useState('monthly');
  const [isActivating, setIsActivating] = useState(false);

  // CSV Export function
  const handleExportCSV = () => {
    if (!tenants || tenants.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no tenants to export.",
        variant: "destructive",
      });
      return;
    }

    // Create CSV headers
    const headers = [
      'Organization Name',
      'Plan Type',
      'Status',
      'User Count',
      'License Count',
      'Owner Name',
      'Owner Email',
      'Created Date',
      'Stripe Customer ID'
    ];

    // Create CSV rows
    const csvData = tenants.map(tenant => [
      tenant.name,
      tenant.planName || tenant.planType,
      tenant.status,
      tenant.userCount || 0,
      tenant.licenseCount || 0,
      tenant.ownerFullName || '',
      tenant.ownerEmail || '',
      tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : '',
      tenant.stripeCustomerId || ''
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `saascompass-tenants-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Export successful",
      description: `Exported ${tenants.length} tenant records to CSV`,
    });
  };

  // Fetch tenant data
  const { data: tenants = [], isLoading, refetch } = useQuery({
    queryKey: ["/api/admin/system/tenant-summaries"],
    queryFn: async () => {
      try {
        console.log("Fetching tenant data from API...");
        const response = await apiRequest("/api/admin/system/tenant-summaries", {
          method: "GET",
        });
        console.log("Received tenant data:", response);
        return response as Tenant[];
      } catch (error) {
        console.error("Error fetching tenants:", error);
        toast({
          title: "Failed to fetch tenants",
          description: "Could not retrieve organization data. Please try again.",
          variant: "destructive"
        });
        return [];
      }
    },
  });
  
  // Apply filtering to tenants based on current filters
  const filteredTenants = tenants.filter(tenant => {
    // Apply search filter (removed domain search)
    const email = tenant.ownerEmail || tenant.primaryContactEmail || '';
    const matchesSearch =
      tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply status filter
    const matchesStatus = statusFilter === "all" || tenant.status === statusFilter;
    
    // Apply plan filter
    const matchesPlan = planFilter === "all" || tenant.planType === planFilter;
    
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Calculate pagination
  const totalItems = filteredTenants.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTenants = filteredTenants.slice(startIndex, endIndex);

  // Reset to first page when filters change
  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  // Handle tenant status change
  const handleStatusChange = async (tenantId: number, newStatus: string) => {
    try {
      await apiRequest(`/api/admin/system/tenant/${tenantId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
      toast({
        title: "Status updated",
        description: `Organization status changed to ${newStatus}`
      });
      refetch();
    } catch (error) {
      toast({
        title: "Failed to update status",
        description: "Could not change organization status. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle search with filter reset
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    handleFilterChange();
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    handleFilterChange();
  };

  // Handle plan filter change
  const handlePlanFilterChange = (value: string) => {
    setPlanFilter(value);
    handleFilterChange();
  };

  // Handle tenant creation
  const handleCreateTenant = async () => {
    try {
      await apiRequest('/api/admin/tenant', {
        method: 'POST',
        body: tenantForm
      });
      toast({
        title: "Tenant created",
        description: `New tenant "${tenantForm.name}" has been created`,
      });
      setShowAddTenantDialog(false);
      setTenantForm({
        name: '',
        planType: 'free',
        status: 'active'
      });
      refetch();
    } catch (error) {
      toast({
        title: "Failed to create tenant",
        description: "Could not create the tenant. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle tenant deletion
  const handleDeleteTenant = async (tenantId: number) => {
    try {
      await apiRequest(`/api/admin/tenant/${tenantId}`, {
        method: "DELETE"
      });
      toast({
        title: "Tenant deleted",
        description: "The tenant has been permanently deleted",
        variant: "destructive"
      });
      refetch();
    } catch (error) {
      toast({
        title: "Failed to delete tenant",
        description: "Could not delete the tenant. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle tenant status update
  const handleStatusUpdate = async (tenantId: number, newStatus: string) => {
    try {
      await apiRequest(`/api/admin/system/tenant/${tenantId}/status`, {
        method: 'PATCH',
        body: { status: newStatus }
      });
      toast({
        title: "Status updated",
        description: `Organization status has been updated to ${newStatus}`,
      });
      refetch();
    } catch (error) {
      console.error('Status update error:', error);
      toast({
        title: "Failed to update status",
        description: "Could not change organization status. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handle manual plan activation (cash/cheque payments)
  const handleManualActivate = async () => {
    if (!activateDialogTenant) return;
    setIsActivating(true);
    try {
      await apiRequest(`/api/admin/system/tenant/${activateDialogTenant.id}/manual-activate`, {
        method: 'POST',
        body: { plan: activatePlan, billingCycle: activateBilling },
      });
      toast({
        title: 'Account activated',
        description: `${activateDialogTenant.name} is now on the ${activatePlan} plan`,
      });
      setActivateDialogTenant(null);
      refetch();
    } catch (error) {
      toast({
        title: 'Activation failed',
        description: 'Could not activate the account. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsActivating(false);
    }
  };

  // Handle view tenant details
  const handleViewDetails = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsViewingDetails(true);
  };



  // Render status badge with appropriate color
  const getStatusBadge = (tenant: Tenant) => {
    if (isTrialExpired(tenant)) {
      return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Trial Expired</Badge>;
    }
    switch (tenant.status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>;
      case 'inactive':
        return <Badge variant="outline" className="text-slate-500">Inactive</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Suspended</Badge>;
      case 'trial':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Trial</Badge>;
      case 'frozen':
        return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Frozen</Badge>;
      default:
        return <Badge variant="outline">{tenant.status}</Badge>;
    }
  };

  // Render plan badge with appropriate color
  const getPlanBadge = (planType: string, planName: string) => {
    switch (planType) {
      case 'enterprise':
        return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">{planName}</Badge>;
      case 'team':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">{planName}</Badge>;
      case 'single':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{planName}</Badge>;
      case 'free':
        return <Badge variant="outline" className="text-slate-500">{planName}</Badge>;
      default:
        return <Badge variant="outline">{planName}</Badge>;
    }
  };

  return (
    <SuperAdminLayout>
      <div className="flex flex-col h-screen">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Tenant Management</h1>
              <p className="text-muted-foreground">
                Manage all tenant organizations in the SAASGAMA platform
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Dialog open={showAddTenantDialog} onOpenChange={setShowAddTenantDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Organization
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Create New Organization</DialogTitle>
                    <DialogDescription>
                      Add a new tenant organization to the platform
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="name" className="text-right">
                        Name
                      </Label>
                      <Input
                        id="name"
                        value={tenantForm.name}
                        onChange={(e) => setTenantForm({...tenantForm, name: e.target.value})}
                        className="col-span-3"
                        placeholder="Organization name"
                      />
                    </div>

                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="plan" className="text-right">
                        Plan
                      </Label>
                      <Select 
                        value={tenantForm.planType} 
                        onValueChange={(value) => setTenantForm({...tenantForm, planType: value})}
                      >
                        <SelectTrigger className="col-span-3">
                          <SelectValue placeholder="Select plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="team">Team</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCreateTenant}>Create Organization</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

        {/* Filters and Search */}
        <div className="flex-shrink-0 p-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Organizations
                <Badge variant="outline" className="ml-2">
                  {totalItems} organizations
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search organizations..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                    <SelectTrigger className="w-[140px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={planFilter} onValueChange={handlePlanFilterChange}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="All Plans" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Plans</SelectItem>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table Container - Scrollable */}
        <div className="flex-1 px-6 pb-6">
          <div className="border rounded-lg bg-white" style={{ height: '800px' }}>
            <div className="h-full overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-[180px]">Organization</TableHead>
                    <TableHead>Owner Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Users</TableHead>
                    <TableHead className="text-center">Licenses</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        Loading organizations...
                      </TableCell>
                    </TableRow>
                  ) : paginatedTenants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        No organizations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTenants.map((tenant) => (
                      <TableRow
                        key={tenant.id}
                        className="h-16 cursor-pointer hover:bg-slate-50"
                        onClick={() => handleViewDetails(tenant)}
                      >
                        <TableCell className="font-medium py-3">
                          <div className="flex items-center space-x-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span>{tenant.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                            <Mail className="h-4 w-4 shrink-0" />
                            <span className="truncate max-w-[180px]">{tenant.ownerEmail || tenant.primaryContactEmail || '—'}</span>
                          </div>
                          {tenant.otherEmails?.map((email) => (
                            <div key={email} className="flex items-center space-x-1 text-xs text-muted-foreground mt-1">
                              <Mail className="h-3 w-3 shrink-0" />
                              <span className="truncate max-w-[180px]">{email}</span>
                            </div>
                          ))}
                        </TableCell>
                        <TableCell className="py-3">
                          {getPlanBadge(tenant.planType, tenant.planName)}
                        </TableCell>
                        <TableCell className="py-3">
                          {getStatusBadge(tenant)}
                        </TableCell>
                        <TableCell className="text-center py-3">
                          <div className="flex items-center justify-center space-x-1">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>{tenant.userCount}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center py-3">
                          <div className="flex items-center justify-center space-x-1">
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                            <span>{tenant.licenseCount}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>{new Date(tenant.createdAt).toLocaleDateString()}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center py-3" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleViewDetails(tenant)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {tenant.status === 'active' ? (
                                <DropdownMenuItem
                                  onClick={() => handleStatusUpdate(tenant.id, 'inactive')}
                                  className="text-orange-600"
                                >
                                  <ShieldCheck className="h-4 w-4 mr-2" />
                                  Deactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => { setActivateDialogTenant(tenant); setActivatePlan('single'); setActivateBilling('monthly'); }}
                                  className="text-green-600"
                                >
                                  <ShieldCheck className="h-4 w-4 mr-2" />
                                  Activate Account
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem 
                                    className="text-red-600 cursor-pointer"
                                    onSelect={(e) => e.preventDefault()}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Organization</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{tenant.name}"? This action cannot be undone and will permanently remove all associated data.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleDeleteTenant(tenant.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex-shrink-0 px-6 pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <p className="text-sm text-muted-foreground">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} organizations
                </p>
                <Select 
                  value={pageSize.toString()} 
                  onValueChange={(value) => {
                    setPageSize(parseInt(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">per page</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* View Details Dialog */}
      <Dialog open={isViewingDetails} onOpenChange={setIsViewingDetails}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Tenant Details</DialogTitle>
            <DialogDescription>
              Complete registration and subscription information for {selectedTenant?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTenant && (
            <div className="grid gap-6 overflow-y-auto flex-1 pr-2">
              {/* Registration Information */}
              <div>
                <h4 className="font-medium mb-3">Registration Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Full Name</Label>
                    <p className="text-sm font-medium">{selectedTenant.ownerFullName || 'Not available'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Company Name</Label>
                    <p className="text-sm font-medium">{selectedTenant.name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                    <p className="text-sm">{selectedTenant.ownerEmail || 'Not available'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Account Identifier</Label>
                    <p className="text-sm font-mono text-xs">{selectedTenant.ownerEmail || 'Not available'}</p>
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Organization Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedTenant)}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Plan Type</Label>
                    <div className="mt-1">{getPlanBadge(selectedTenant.planType, selectedTenant.planName)}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Registration Date</Label>
                    <p className="text-sm">{new Date(selectedTenant.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Tenant ID</Label>
                    <p className="text-sm font-semibold">#{selectedTenant.id}</p>
                  </div>
                </div>
              </div>

              {/* Usage Statistics */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Usage Statistics</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Users</Label>
                    <p className="text-2xl font-semibold">{selectedTenant.userCount}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Licenses</Label>
                    <p className="text-2xl font-semibold">{selectedTenant.licenseCount}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Tenant ID</Label>
                    <p className="text-2xl font-semibold">#{selectedTenant.id}</p>
                  </div>
                </div>
              </div>

              {/* Subscription Information */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Subscription Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Stripe Customer ID</Label>
                    <p className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {selectedTenant.stripeCustomerId || 'Not connected'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Current Plan</Label>
                    <p className="text-sm font-semibold">{selectedTenant.planName}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Manual Activation Dialog */}
      <Dialog open={!!activateDialogTenant} onOpenChange={(open) => { if (!open) setActivateDialogTenant(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Activate Account</DialogTitle>
            <DialogDescription>
              Manually activate <strong>{activateDialogTenant?.name}</strong> for a cash or cheque payment. Select the plan and billing cycle.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Plan</Label>
              <Select value={activatePlan} onValueChange={setActivatePlan}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single — $29/month</SelectItem>
                  <SelectItem value="team">Team — $49/month</SelectItem>
                  <SelectItem value="enterprise">Enterprise — $199/month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Billing</Label>
              <Select value={activateBilling} onValueChange={setActivateBilling}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly (1 month)</SelectItem>
                  <SelectItem value="annual">Annual (12 months)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivateDialogTenant(null)}>Cancel</Button>
            <Button onClick={handleManualActivate} disabled={isActivating} className="bg-green-600 hover:bg-green-700">
              {isActivating ? 'Activating…' : 'Activate Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </SuperAdminLayout>
  );
}